import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useKid } from "@/context/KidContext";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Sparkles, Flame, Star, Award, Target } from "lucide-react";

const CARDS = [
  { key: "atividade-hoje", to: "/library?category=atividades", emoji: "⭐", title: "Atividade de Hoje", color: "#FFD54F" },
  { key: "historia", to: "/library?category=historias", emoji: "📖", title: "História Bíblica", color: "#5DADE2" },
  { key: "criar", to: "/library?category=colorir", emoji: "🎨", title: "Criar & Brincar", color: "#D9A441" },
  { key: "louvor", to: "/library?category=musicas", emoji: "🎵", title: "Louvor", color: "#7BC67B" },
  { key: "momento", to: "/library?category=oracoes", emoji: "🙏", title: "Momento com Deus", color: "#FFD54F" },
  { key: "biblioteca", to: "/library", emoji: "📚", title: "Biblioteca", color: "#5DADE2" },
];

export default function HomeApp() {
  const { user } = useAuth();
  const { active, kids } = useKid();
  const [featured, setFeatured] = useState([]);
  const [versiculo, setVersiculo] = useState(null);
  const [series, setSeries] = useState([]);
  const [continueItems, setContinueItems] = useState([]);
  const [home, setHome] = useState(null);

  useEffect(() => {
    api.get("/contents", { params: { featured: true } }).then(({ data }) => setFeatured(data.contents.slice(0, 4)));
    api.get("/contents", { params: { category: "versiculos" } }).then(({ data }) => setVersiculo(data.contents[0]));
    api.get("/series").then(({ data }) => setSeries((data.series || []).slice(0, 3)));
  }, []);

  useEffect(() => {
    if (active) {
      api.get(`/continue-watching/${active.id}`).then(({ data }) => setContinueItems(data.items || []));
      api.get(`/home/${active.id}`).then(({ data }) => setHome(data)).catch(() => setHome(null));
    } else {
      setContinueItems([]); setHome(null);
    }
  }, [active]);

  const completeMission = async (key) => {
    if (!active) return;
    try {
      const { data } = await api.post(`/missions/${key}/complete`, { child_id: active.id });
      if (!data.already_awarded) {
        // eslint-disable-next-line
        (await import("sonner")).toast.success(`+${data.awarded_points} pontos! Missão concluída 🎯`);
      }
      const { data: fresh } = await api.get(`/home/${active.id}`);
      setHome(fresh);
    } catch { /* ignore */ }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
      {/* Greeting */}
      <section className="grid lg:grid-cols-3 gap-6 items-stretch">
        <div className="lg:col-span-2 card-soft p-6 sm:p-8 bg-gradient-to-br from-[#FFD54F]/40 to-[#5DADE2]/20">
          <span className="chip chip-gold">Olá, {user?.name?.split(" ")[0] || "amigo"} 👋</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#243B53]">Olá! Vamos descobrir algo novo hoje?</h1>
          <p className="mt-2 text-[#243B53]/75">Escolha um cardinho abaixo para começar sua aventura de fé.</p>
          {kids.length === 0 && (
            <Link data-testid="home-add-child-cta" to="/profile" className="btn-primary mt-6 inline-block">Cadastrar minha primeira criança</Link>
          )}
        </div>
        <KidSummary active={active} />
      </section>

      {/* Verse */}
      {versiculo && (
        <section className="mt-8 card-soft p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-4">
          <div className="text-4xl">✨</div>
          <div>
            <div className="text-sm font-bold text-[#D9A441]">Versículo do Dia</div>
            <div className="mt-1 text-xl font-extrabold text-[#243B53]">{versiculo.title}</div>
            <div className="mt-2 text-[#243B53]/80 italic">{versiculo.body}</div>
          </div>
        </section>
      )}

      {/* Themed cards */}
      <section className="mt-10">
        <h2 className="text-xl sm:text-2xl font-extrabold text-[#243B53]">Por onde começar?</h2>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {CARDS.map((c) => (
            <Link key={c.key} to={c.to} data-testid={`home-card-${c.key}`}
              className="card-soft p-5 text-center hover:scale-[1.03] transition-transform"
              style={{ borderTop: `4px solid ${c.color}` }}>
              <div className="text-4xl">{c.emoji}</div>
              <div className="mt-2 font-extrabold text-[#243B53] text-sm">{c.title}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Continue watching */}
      {continueItems.length > 0 && (
        <section className="mt-10" data-testid="home-continue-watching">
          <div className="flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-extrabold text-[#243B53]">▶️ Continuar assistindo</h2>
            <Link to="/series" data-testid="home-see-series-link" className="text-[#5DADE2] font-bold text-sm">Ver todas →</Link>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {continueItems.slice(0, 2).map(({ series: s, next_episode, completed, total }) => (
              <Link key={s.key} to={`/content/${next_episode.id}`} data-testid={`home-continue-${s.key}`}
                className="card-soft p-4 flex gap-4 items-center hover:-translate-y-0.5 transition">
                <div className="w-24 h-16 rounded-2xl overflow-hidden bg-[#FFF9ED] shrink-0">
                  {s.cover_image ? <img src={s.cover_image} alt={s.title} className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-3xl">📺</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-[#243B53]/60">{s.title}</div>
                  <div className="font-extrabold text-[#243B53] truncate">{next_episode.title}</div>
                  <div className="mt-1 progress-track"><div className="progress-fill" style={{ width: `${Math.round(completed/Math.max(1,total)*100)}%` }}/></div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Missions */}
      {home?.todays_mission && (
        <section className="mt-10" data-testid="home-missions">
          <div className="flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-extrabold text-[#243B53]">🎯 Missão de hoje</h2>
          </div>
          <div className="mt-4 card-soft p-6 flex flex-col sm:flex-row items-start gap-4 bg-gradient-to-br from-[#FFD54F]/20 to-transparent">
            <div className="text-5xl">{home.todays_mission.icon}</div>
            <div className="flex-1">
              <div className="font-extrabold text-[#243B53] text-lg">{home.todays_mission.title}</div>
              <div className="text-sm text-[#243B53]/70">{home.todays_mission.description}</div>
              <div className="mt-2 text-xs text-[#243B53]/60">+{home.todays_mission.points_reward} pts • +{home.todays_mission.stars_reward} ⭐</div>
            </div>
            <button data-testid="home-mission-complete-btn" onClick={() => completeMission(home.todays_mission.key)} className="btn-primary text-sm">
              Marcar feita
            </button>
          </div>
        </section>
      )}

      {/* Journeys in progress */}
      {(home?.journeys_in_progress?.length > 0 || home?.journeys_available?.length > 0) && (
        <section className="mt-10" data-testid="home-journeys">
          <div className="flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-extrabold text-[#243B53]">🧭 Jornadas</h2>
            <Link to="/journeys" className="text-[#5DADE2] font-bold text-sm">Ver todas →</Link>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...(home.journeys_in_progress||[]), ...(home.journeys_available||[]).map(j => ({journey:j, progress:null}))].slice(0,3).map(({journey: j, progress: p}) => (
              <Link key={j.key} to={`/journeys/${j.key}`} data-testid={`home-journey-${j.key}`}
                className="card-soft p-4 hover:-translate-y-0.5 transition block">
                <div className="font-extrabold text-[#243B53]">{j.title}</div>
                <div className="text-xs text-[#243B53]/60 mt-1">{j.duration_days} dias • {(j.steps||[]).length} etapas</div>
                {p && <div className="mt-2 progress-track"><div className="progress-fill" style={{ width: `${((p.completed_step_numbers||[]).length/Math.max(1,(j.steps||[]).length))*100}%` }}/></div>}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Series */}
      {series.length > 0 && (
        <section className="mt-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-extrabold text-[#243B53]">📺 Séries do Clubinho</h2>
            <Link to="/series" data-testid="home-series-all-link" className="text-[#5DADE2] font-bold text-sm">Ver todas as séries →</Link>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {series.map((s) => (
              <Link key={s.key} to={`/series/${s.key}`} data-testid={`home-series-${s.key}`}
                className="card-soft overflow-hidden hover:-translate-y-1 transition block">
                <div className="aspect-[16/9] bg-[#FFF9ED] relative">
                  {s.cover_image ? <img src={s.cover_image} alt={s.title} className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-5xl">📺</div>}
                  <div className="absolute bottom-0 left-0 right-0 h-1 rainbow-strip opacity-90" />
                </div>
                <div className="p-4">
                  <div className="font-extrabold text-[#243B53]">{s.title}</div>
                  <div className="mt-1 text-xs text-[#243B53]/60">{s.episode_count || 0} episódios</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured */}
      <section className="mt-12">
        <div className="flex items-center justify-between">
          <h2 className="text-xl sm:text-2xl font-extrabold text-[#243B53]">Destaques da semana</h2>
          <Link to="/library" data-testid="home-see-all-link" className="text-[#5DADE2] font-bold text-sm">Ver todos →</Link>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {featured.map((c) => (
            <ContentCard key={c.id} content={c} />
          ))}
        </div>
      </section>
    </div>
  );
}

function KidSummary({ active }) {
  if (!active) {
    return (
      <div className="card-soft p-6 text-center">
        <div className="text-4xl">🧒</div>
        <div className="mt-2 font-extrabold text-[#243B53]">Nenhuma criança ainda</div>
        <div className="text-sm text-[#243B53]/70 mt-1">Cadastre no seu perfil.</div>
      </div>
    );
  }
  return (
    <div className="card-soft p-6" data-testid="home-kid-summary">
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-[#FFD54F] flex items-center justify-center text-2xl">🧒</div>
        <div>
          <div className="text-xs font-bold text-[#243B53]/60">Criança ativa</div>
          <div className="font-extrabold text-[#243B53] text-lg">{active.name}</div>
          <div className="text-xs text-[#243B53]/60">{active.age} anos • Faixa {active.age_group}</div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <Stat icon={<Star size={16} />} label="Estrelas" value={active.stars || 0} />
        <Stat icon={<Sparkles size={16} />} label="Pontos" value={active.points || 0} />
        <Stat icon={<Flame size={16} />} label="Sequência" value={`${active.streak_days || 0}d`} />
      </div>
      <div className="mt-4 card-soft !shadow-none border-[#243B53]/10 p-3 flex items-center gap-2 bg-[#FFF9ED]">
        <div className="text-2xl">{active.rank?.emoji}</div>
        <div>
          <div className="text-xs font-bold text-[#243B53]/60">Passaporte</div>
          <div className="font-extrabold text-[#243B53]">{active.rank?.label}</div>
        </div>
        <Link to="/passport" className="ml-auto text-[#5DADE2] font-bold text-sm">Ver</Link>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="bg-[#FFF9ED] rounded-2xl py-2">
      <div className="flex items-center justify-center gap-1 text-[#D9A441]">{icon}</div>
      <div className="font-extrabold text-[#243B53]">{value}</div>
      <div className="text-[10px] font-bold text-[#243B53]/60">{label}</div>
    </div>
  );
}

export function ContentCard({ content }) {
  return (
    <Link to={`/content/${content.id}`} data-testid={`content-card-${content.id}`} className="card-soft overflow-hidden block">
      <div className="aspect-[4/3] bg-[#FFF9ED] overflow-hidden">
        {content.cover_image ? (
          <img src={content.cover_image} alt={content.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl">📖</div>
        )}
      </div>
      <div className="p-4">
        <div className="flex flex-wrap gap-1">
          <span className="chip chip-sun capitalize">{content.category}</span>
          {content.theme && <span className="chip">{content.theme}</span>}
        </div>
        <h3 className="mt-2 font-extrabold text-[#243B53]">{content.title}</h3>
        <div className="text-xs text-[#243B53]/60 mt-1">{content.duration_min} min • {content.age_groups?.join(", ")}</div>
      </div>
    </Link>
  );
}
